import { useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { useTheme } from 'components/core/theme-provider';
import { cn } from 'lib/utils';
import { getPickerTheme } from 'pages/agents/utils/agent-config-picker-theme';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { AgentSoulSection } from 'features/agent-soul/components/agent-soul-section';
import { isAgentSoulHidden, useAgentSoul } from 'features/agent-soul/hooks/use-agent-soul';
import { CONFIG_SECTIONS } from './agent-configuration.constants';
import { AgentConfigSectionActions } from './agent-config-section-actions';
import { AgentConfigAddToolModal } from './agent-config-add-tool-modal';
import { AgentConfigAddSkillModal } from './agent-config-add-skill-modal';
import { AgentConfigAddSubagentModal } from './agent-config-add-subagent-modal';
import { AgentConfigAddKBModal } from './knowledge-base-section/agents-config-add-kb-modal';
import { KnowledgeBaseSection } from './knowledge-base-section';
import { LlmConfigurationSection } from './llm-configuration-section-panel';
import { MemorySection } from './memory-section';
// import { SafetyMechanismSection } from './safety-mechanism-section';
import { GuardrailsSection } from './guardrails-section';
import { AgentConfigAddGuardrailModal } from './agent-config-add-guardrail-modal';
import { ToolsSection } from './tools-section';
import { SkillsSection } from './skills-section';
import { SubagentsSection } from './subagents-section';
import { WelcomeGuideSection } from './welcome-guide-section';
import type {
  IGetAgentModelResponse,
  ITool,
} from 'features/chat/agent-chat-module/services/gpt-chat.service';
import type {
  Agent,
  AgentSummary,
  CustomRule,
  GuardrailCatalogItem,
  GuardrailConfig,
  GuardrailOutputStrategy,
} from 'features/chat/agent-chat-module/types/agent.service.type';
import type { SkillListItem } from 'pages/skills/skills.types';
import {
  canEditAgentConfig,
  canManageKb,
  canManageSkills,
  canManageSubagents,
  canManageTools,
  isAgentOwnerAccess,
} from '../utils/agent-share-access';

/** Plain readable copy when UILM keys are missing or still untranslated. */
const configText = (
  t: TFunction,
  key: string,
  fallback: string,
  options?: Record<string, unknown>
) => {
  const value = t(key, { defaultValue: fallback, ...options });
  if (!value || value === key || /^[A-Z][A-Z0-9_]+$/.test(String(value))) {
    return fallback;
  }
  return String(value);
};

type AgentConfigurationCardProps = {
  agentId: string;
  isAgentDetailsLoading?: boolean;
  sectionState: Record<string, boolean>;
  onSectionStateChange: (key: string, checked: boolean) => void;
  isLoadingLlmModels: boolean;
  availableChatModels: IGetAgentModelResponse;
  availableCustomChatModels: IGetAgentModelResponse;
  currentLlmModel: string;
  currentLlmProvider: string;
  currentLlmModelId: string;
  isBlocksProvider: boolean;
  onModelChange: (value: {
    modelName: string;
    provider: string;
    isBlocksProvider: boolean;
    modelId?: string | null;
    temperature: number;
    maxTokens: number;
  }) => void;
  responseType: string;
  onResponseTypeChange: (value: string) => void;
  responseFormat: string | null;
  onResponseFormatChange: (value: string | null) => void;
  basePrompt: string;
  onBasePromptChange: (value: string) => void;
  autoBasePrompt: string;
  temperatureValue: number;
  onTemperatureChange: (value: number) => void;
  maxResponseValue: number;
  onMaxResponseChange: (value: number) => void;
  isModelParametersOpen: boolean;
  onModelParametersOpenChange: (open: boolean) => void;
  enableQueryEnhancement: boolean;
  onEnableQueryEnhancementChange: (value: boolean) => void;
  scoreThreshold: number;
  onScoreThresholdChange: (value: number) => void;
  topK: number;
  onTopKChange: (value: number) => void;
  denseWeight: number;
  onDenseWeightChange: (value: number) => void;
  projectKey: string;
  selectedFolderIds: string[];
  onSelectedFolderIdsChange: (value: string[]) => void;
  selectedToolIds: string[];
  onSelectedToolIdsChange: (value: string[]) => void;
  selectedSkillIds: string[];
  onSelectedSkillIdsChange: (value: string[]) => void;
  selectedSubagentIds: string[];
  onSelectedSubagentIdsChange: (value: string[]) => void;
  enableSummary: boolean;
  onEnableSummaryChange: (value: boolean) => void;
  lastNConversations: number;
  onLastNConversationsChange: (value: number) => void;
  agentName?: string;
  greeting: string;
  onGreetingChange: (value: string) => void;
  enableSmartSuggestions: boolean;
  onEnableSmartSuggestionsChange: (value: boolean) => void;
  enablePresetQuestions: boolean;
  onEnablePresetQuestionsChange: (value: boolean) => void;
  presetQuestions: string[];
  onPresetQuestionsChange: (questions: string[]) => void;
  guardrailOutputStrategy?: GuardrailOutputStrategy;
  onGuardrailOutputStrategyChange?: (value: GuardrailOutputStrategy) => void;
  configuredGuardrails?: Record<string, GuardrailConfig>;
  onConfiguredGuardrailsChange?: (value: Record<string, GuardrailConfig>) => void;
  guardrailErrors?: Record<string, string>;
  // Previous safety mechanism props preserved:
  enablePreValidation?: boolean;
  onEnablePreValidationChange?: (value: boolean) => void;
  enablePostValidation?: boolean;
  onEnablePostValidationChange?: (value: boolean) => void;
  enablePiiDetection?: boolean;
  onEnablePiiDetectionChange?: (value: boolean) => void;
  enableInjectionDetection?: boolean;
  onEnableInjectionDetectionChange?: (value: boolean) => void;
  inputRiskThreshold?: number;
  onInputRiskThresholdChange?: (value: number) => void;
  outputRiskThreshold?: number;
  onOutputRiskThresholdChange?: (value: number) => void;
  enableKeywordCheck?: boolean;
  onEnableKeywordCheckChange?: (value: boolean) => void;
  bannedKeywords?: string[];
  onBannedKeywordsChange?: (value: string[]) => void;
  bannedPatterns?: string[];
  onBannedPatternsChange?: (value: string[]) => void;
  enableCustomRules?: boolean;
  onEnableCustomRulesChange?: (value: boolean) => void;
  customRules?: CustomRule[];
  onCustomRulesChange?: (value: CustomRule[]) => void;
  agent?: Agent | null;
  /** @deprecated Prefer `agent` + myShareAccess; kept for call-site compatibility. */
  createdBy?: string | null;
  headerAction?: ReactNode;
};

export function AgentConfigurationCard({
  agentId,
  isAgentDetailsLoading = false,
  sectionState,
  onSectionStateChange,
  isLoadingLlmModels,
  availableChatModels,
  availableCustomChatModels,
  currentLlmModel,
  currentLlmProvider,
  currentLlmModelId,
  isBlocksProvider,
  onModelChange,
  responseType,
  onResponseTypeChange,
  responseFormat,
  onResponseFormatChange,
  basePrompt,
  onBasePromptChange,
  autoBasePrompt,
  temperatureValue,
  onTemperatureChange,
  maxResponseValue,
  onMaxResponseChange,
  isModelParametersOpen,
  onModelParametersOpenChange,
  enableQueryEnhancement,
  onEnableQueryEnhancementChange,
  scoreThreshold,
  onScoreThresholdChange,
  topK,
  onTopKChange,
  denseWeight,
  onDenseWeightChange,
  projectKey,
  selectedFolderIds,
  onSelectedFolderIdsChange,
  selectedToolIds,
  onSelectedToolIdsChange,
  selectedSkillIds,
  onSelectedSkillIdsChange,
  selectedSubagentIds,
  onSelectedSubagentIdsChange,
  enableSummary,
  onEnableSummaryChange,
  lastNConversations,
  onLastNConversationsChange,
  agentName,
  greeting,
  onGreetingChange,
  enableSmartSuggestions,
  onEnableSmartSuggestionsChange,
  enablePresetQuestions,
  onEnablePresetQuestionsChange,
  presetQuestions,
  onPresetQuestionsChange,
  guardrailOutputStrategy = 'block',
  onGuardrailOutputStrategyChange,
  configuredGuardrails = {},
  onConfiguredGuardrailsChange,
  guardrailErrors,
  enablePreValidation,
  onEnablePreValidationChange,
  enablePostValidation,
  onEnablePostValidationChange,
  enablePiiDetection,
  onEnablePiiDetectionChange,
  enableInjectionDetection,
  onEnableInjectionDetectionChange,
  inputRiskThreshold,
  onInputRiskThresholdChange,
  outputRiskThreshold,
  onOutputRiskThresholdChange,
  enableKeywordCheck,
  onEnableKeywordCheckChange,
  bannedKeywords,
  onBannedKeywordsChange,
  bannedPatterns,
  onBannedPatternsChange,
  enableCustomRules,
  onEnableCustomRulesChange,
  customRules,
  onCustomRulesChange,
  agent,
  createdBy,
  headerAction,
}: AgentConfigurationCardProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const ui = getPickerTheme(theme === 'dark');
  const userId = useCommonStore((state) => state.user?.id);
  const shareAgent = agent ?? (createdBy ? { created_by: createdBy } : null);
  const isOwner = isAgentOwnerAccess(shareAgent, userId);
  const canEdit = canEditAgentConfig(shareAgent, userId);
  const manageTools = canManageTools(shareAgent, userId);
  const manageSkills = canManageSkills(shareAgent, userId);
  const manageSubagents = canManageSubagents(shareAgent, userId);
  const manageKb = canManageKb(shareAgent, userId);
  const isOwnershipPending = isAgentDetailsLoading || !shareAgent || userId == null;
  const { data: soul, error: soulError } = useAgentSoul(agentId);
  const isSoulHidden = isAgentSoulHidden(soul, soulError);

  const canManageSection = (key: string) => {
    if (key === 'tools') return manageTools;
    if (key === 'skills') return manageSkills;
    if (key === 'subagents') return manageSubagents;
    if (key === 'knowledge-base') return manageKb;
    if (key === 'welcome-guide') return isOwner;
    // LLM / guardrails / memory follow canEditConfig
    return canEdit;
  };

  const configSubtitle = isOwnershipPending
    ? configText(t, 'CREATE_YOUR_OWN_UNIQUE_SETUP', 'Create your own unique setup')
    : !canEdit
      ? configText(
        t,
        'YOU_ARE_NOT_THE_OWNER_OF_THIS_AGENT',
        'You are not the owner of this agent.'
      )
      : !isOwner
        ? configText(t, 'SHARED_WITH_YOU_CAN_EDIT', 'Shared with you · You can edit')
        : configText(t, 'CREATE_YOUR_OWN_UNIQUE_SETUP', 'Create your own unique setup');
  const [isAddKnowledgeBaseModalOpen, setIsAddKnowledgeBaseModalOpen] = useState(false);
  const [isAddToolModalOpen, setIsAddToolModalOpen] = useState(false);
  const [isAddSkillModalOpen, setIsAddSkillModalOpen] = useState(false);
  const [isAddSubagentModalOpen, setIsAddSubagentModalOpen] = useState(false);
  const [isAddGuardrailModalOpen, setIsAddGuardrailModalOpen] = useState(false);

  const handleAddGuardrail = (item: GuardrailCatalogItem) => {
    if (configuredGuardrails && configuredGuardrails[item.id]) {
      return;
    }
    const updated = {
      ...configuredGuardrails,
      [item.id]: {
        enabled: true,
        ...(item.params?.keywords ? { keywords: [] } : {}),
      },
    };
    onConfiguredGuardrailsChange?.(updated);
    onSectionStateChange('guardrails', true);
  };

  const handleRemoveGuardrail = (guardrailId: string) => {
    const updated = { ...configuredGuardrails };
    delete updated[guardrailId];
    onConfiguredGuardrailsChange?.(updated);
    if (Object.keys(updated).length === 0) {
      onSectionStateChange('guardrails', false);
    }
  };

  const handleAddKnowledgeBase = (id: string) => {
    if (selectedFolderIds.includes(id)) {
      return;
    }

    onSelectedFolderIdsChange([...selectedFolderIds, id]);
  };

  const handleRemoveKnowledgeBase = (id: string) => {
    onSelectedFolderIdsChange(selectedFolderIds.filter((folderId) => folderId !== id));
  };

  const handleAddTool = (tool: ITool) => {
    if (selectedToolIds.includes(tool.id)) {
      return;
    }

    onSelectedToolIdsChange([...selectedToolIds, tool.id]);
    onSectionStateChange('tools', true);
  };

  const handleRemoveTool = (toolId: string) => {
    const nextIds = selectedToolIds.filter((id) => id !== toolId);
    onSelectedToolIdsChange(nextIds);
  };

  const handleAddSkill = (skill: SkillListItem) => {
    if (selectedSkillIds.includes(skill.id)) {
      return;
    }

    onSelectedSkillIdsChange([...selectedSkillIds, skill.id]);
    onSectionStateChange('skills', true);
  };

  const handleRemoveSkill = (skillId: string) => {
    const nextIds = selectedSkillIds.filter((id) => id !== skillId);
    onSelectedSkillIdsChange(nextIds);
  };

  const handleAddSubagent = (subagent: AgentSummary) => {
    if (selectedSubagentIds.includes(subagent.id)) {
      return;
    }

    onSelectedSubagentIdsChange([...selectedSubagentIds, subagent.id]);
    onSectionStateChange('subagents', true);
  };

  const handleRemoveSubagent = (subagentId: string) => {
    const nextIds = selectedSubagentIds.filter((id) => id !== subagentId);
    onSelectedSubagentIdsChange(nextIds);
  };

  return (
    <div
      data-testid="agent-configuration-card"
      className={cn(
        'flex w-full min-w-0 max-w-full flex-col overflow-hidden rounded-3xl border shadow-sm lg:max-h-[calc(100dvh-8rem)]',
        ui.dialog,
        isAgentDetailsLoading && 'pointer-events-none opacity-60',
      )}
    >
      <div className={cn('shrink-0 px-4 pb-4 pt-5 sm:pt-6', ui.header)}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className={cn('text-2xl font-bold leading-tight tracking-tight sm:text-[1.65rem]', ui.title)}>
              {configText(t, 'CONFIGURATION', 'Configuration')}
            </h2>
            <p className={cn('mt-2 text-sm leading-relaxed', ui.subtitle)}>{configSubtitle}</p>
          </div>
          {headerAction}
        </div>
      </div>
      <div
        className={cn(
          'flex min-h-0 w-full min-w-0 flex-1 flex-col gap-6 overflow-x-hidden overflow-y-auto px-4 py-5',
          '[scrollbar-width:thin] [scrollbar-color:theme(colors.grey.200)_transparent]',
          '[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent',
          '[&::-webkit-scrollbar-thumb]:min-h-12 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-grey-200',
        )}
      >
        <div className="flex w-full min-w-0 max-w-full flex-col gap-6 pb-2 [&_fieldset]:min-w-0 [&_input]:min-w-0 [&_textarea]:min-w-0">
          {CONFIG_SECTIONS.map((section) => {
            if (section.key === 'soul' && isSoulHidden) return null;
            const isComingSoon = !!section.comingSoon;
            const sectionTitle = configText(
              t,
              section.titleKey,
              section.titleFallback || section.titleKey
            );

            // Keyed so a draft, conflict or refusal never carries over to another agent.
            const soulContent =
              section.key === 'soul' ? (
                <AgentSoulSection key={agentId} agentId={agentId} canEdit={canEdit} />
              ) : null;

            const sectionContent =
              soulContent ??
              (section.key === 'llm' ? (
                <LlmConfigurationSection
                  isLoadingLlmModels={isLoadingLlmModels}
                  availableChatModels={availableChatModels}
                  availableCustomChatModels={availableCustomChatModels}
                  currentLlmModel={currentLlmModel}
                  currentLlmProvider={currentLlmProvider}
                  currentLlmModelId={currentLlmModelId}
                  isBlocksProvider={isBlocksProvider}
                  onModelChange={onModelChange}
                  responseType={responseType}
                  onResponseTypeChange={onResponseTypeChange}
                  responseFormat={responseFormat}
                  onResponseFormatChange={onResponseFormatChange}
                  basePrompt={basePrompt}
                  onBasePromptChange={onBasePromptChange}
                  autoBasePrompt={autoBasePrompt}
                  temperatureValue={temperatureValue}
                  onTemperatureChange={onTemperatureChange}
                  maxResponseValue={maxResponseValue}
                  onMaxResponseChange={onMaxResponseChange}
                  isModelParametersOpen={isModelParametersOpen}
                  onModelParametersOpenChange={onModelParametersOpenChange}
                  agentName={agentName}
                  selectedToolIds={selectedToolIds}
                  disabled={!canEdit}
                />
              ) : section.key === 'knowledge-base' ? (
                <KnowledgeBaseSection
                  enabled={manageKb && !!sectionState[section.key]}
                  canEdit={manageKb}
                  projectKey={projectKey}
                  selectedFolderIds={selectedFolderIds}
                  onSelectedFolderIdsChange={onSelectedFolderIdsChange}
                  enableQueryEnhancement={enableQueryEnhancement}
                  onEnableQueryEnhancementChange={onEnableQueryEnhancementChange}
                  scoreThreshold={scoreThreshold}
                  onScoreThresholdChange={onScoreThresholdChange}
                  topK={topK}
                  onTopKChange={onTopKChange}
                  denseWeight={denseWeight}
                  onDenseWeightChange={onDenseWeightChange}
                />
              ) : section.key === 'tools' ? (
                <ToolsSection
                  enabled={manageTools && !!sectionState[section.key]}
                  canEdit={manageTools}
                  selectedToolIds={selectedToolIds}
                  attachedTools={agent?.tools}
                  onRemoveTool={handleRemoveTool}
                />
              ) : section.key === 'skills' ? (
                <SkillsSection
                  enabled={manageSkills && !!sectionState[section.key]}
                  canEdit={manageSkills}
                  selectedSkillIds={selectedSkillIds}
                  onRemoveSkill={handleRemoveSkill}
                />
              ) : section.key === 'subagents' ? (
                <SubagentsSection
                  enabled={manageSubagents && !!sectionState[section.key]}
                  canEdit={manageSubagents}
                  projectKey={projectKey}
                  selectedSubagentIds={selectedSubagentIds}
                  onRemoveSubagent={handleRemoveSubagent}
                />
              ) : section.key === 'memory' ? (
                <MemorySection
                  enableSummary={enableSummary}
                  onEnableSummaryChange={onEnableSummaryChange}
                  lastNConversations={lastNConversations}
                  onLastNConversationsChange={onLastNConversationsChange}
                  disabled={!canEdit}
                />
              ) : section.key === 'welcome-guide' ? (
                <WelcomeGuideSection
                  agentName={agentName}
                  greeting={greeting}
                  onGreetingChange={onGreetingChange}
                  enableSmartSuggestions={enableSmartSuggestions}
                  onEnableSmartSuggestionsChange={onEnableSmartSuggestionsChange}
                  enablePresetQuestions={enablePresetQuestions}
                  onEnablePresetQuestionsChange={onEnablePresetQuestionsChange}
                  presetQuestions={presetQuestions}
                  onPresetQuestionsChange={onPresetQuestionsChange}
                  disabled={!isOwner}
                />
              ) : section.key === 'guardrails' ? (
                <GuardrailsSection
                  enabled={canEdit && !!sectionState[section.key]}
                  canEdit={canEdit}
                  outputStrategy={guardrailOutputStrategy}
                  onOutputStrategyChange={onGuardrailOutputStrategyChange}
                  guardrails={configuredGuardrails}
                  onGuardrailsChange={onConfiguredGuardrailsChange}
                  errors={guardrailErrors}
                />
                /*
                <SafetyMechanismSection
                  enabled={canEdit && !!sectionState[section.key]}
                  enablePreValidation={enablePreValidation}
                  onEnablePreValidationChange={onEnablePreValidationChange}
                  enablePostValidation={enablePostValidation}
                  onEnablePostValidationChange={onEnablePostValidationChange}
                  enablePiiDetection={enablePiiDetection}
                  onEnablePiiDetectionChange={onEnablePiiDetectionChange}
                  enableInjectionDetection={enableInjectionDetection}
                  onEnableInjectionDetectionChange={onEnableInjectionDetectionChange}
                  inputRiskThreshold={inputRiskThreshold}
                  onInputRiskThresholdChange={onInputRiskThresholdChange}
                  outputRiskThreshold={outputRiskThreshold}
                  onOutputRiskThresholdChange={onOutputRiskThresholdChange}
                  enableKeywordCheck={enableKeywordCheck}
                  onEnableKeywordCheckChange={onEnableKeywordCheckChange}
                  bannedKeywords={bannedKeywords}
                  onBannedKeywordsChange={onBannedKeywordsChange}
                  bannedPatterns={bannedPatterns}
                  onBannedPatternsChange={onBannedPatternsChange}
                  enableCustomRules={enableCustomRules}
                  onEnableCustomRulesChange={onEnableCustomRulesChange}
                  customRules={customRules}
                  onCustomRulesChange={onCustomRulesChange}
                />
                */
              ) : (
                <div className="rounded-lg border border-grey-100 bg-grey-50 px-5 py-4 text-sm text-grey-500 dark:border-border dark:bg-muted/30 dark:text-muted-foreground">
                  {section.contentKey
                    ? configText(
                      t,
                      section.contentKey,
                      section.contentFallback || section.contentKey
                    )
                    : null}
                </div>
              ));

            return (
              <div
                key={section.key}
                className="flex w-full min-w-0 max-w-full flex-col gap-4 border-b border-grey-100 pb-6 last:border-b-0 dark:border-border"
              >
                <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-2">
                  <div className="flex min-w-0 grow basis-48 items-center gap-x-2.5 gap-y-1">
                    <p className="truncate text-lg font-semibold text-grey-900 dark:text-true-white">
                      {sectionTitle}
                    </p>
                    {section.badgeLabelKey ? (
                      <span className="shrink-0 rounded-full bg-grey-50 px-2.5 py-0.5 text-xs font-medium text-grey-500 dark:bg-muted dark:text-muted-foreground">
                        {configText(
                          t,
                          section.badgeLabelKey,
                          section.badgeLabelFallback || section.badgeLabelKey
                        )}
                      </span>
                    ) : null}
                  </div>

                  {canManageSection(section.key) && (section.hasAdd || section.hasToggle) ? (
                    <AgentConfigSectionActions
                      className="ml-auto"
                      addLabel={configText(t, 'ADD', 'Add')}
                      onAdd={
                        section.hasAdd
                          ? () => {
                            if (section.key === 'knowledge-base') {
                              setIsAddKnowledgeBaseModalOpen(true);
                            }
                            if (section.key === 'tools') {
                              setIsAddToolModalOpen(true);
                            }
                            if (section.key === 'skills') {
                              setIsAddSkillModalOpen(true);
                            }
                            if (section.key === 'subagents') {
                              setIsAddSubagentModalOpen(true);
                            }
                            if (section.key === 'guardrails') {
                              setIsAddGuardrailModalOpen(true);
                            }
                          }
                          : undefined
                      }
                      isAddDisabled={isAgentDetailsLoading || !sectionState[section.key]}
                      toggleLabel={
                        isComingSoon
                          ? `${sectionTitle}: ${configText(t, 'COMING_SOON', 'Coming Soon')}`
                          : sectionTitle
                      }
                      checked={isComingSoon ? false : !!sectionState[section.key]}
                      onCheckedChange={
                        section.hasToggle
                          ? (checked) => {
                            if (isComingSoon || !canManageSection(section.key)) return;
                            onSectionStateChange(section.key, checked);
                          }
                          : undefined
                      }
                      isToggleDisabled={
                        isAgentDetailsLoading || isComingSoon || !canManageSection(section.key)
                      }
                    />
                  ) : null}
                </div>

                <div className="w-full min-w-0">{sectionContent}</div>
              </div>
            );
          })}
        </div>
      </div>

      <AgentConfigAddKBModal
        open={isAddKnowledgeBaseModalOpen}
        onOpenChange={setIsAddKnowledgeBaseModalOpen}
        projectKey={projectKey}
        currentFolderIds={selectedFolderIds}
        onAddKB={handleAddKnowledgeBase}
        onRemoveKB={handleRemoveKnowledgeBase}
      />

      <AgentConfigAddToolModal
        open={isAddToolModalOpen}
        onOpenChange={setIsAddToolModalOpen}
        selectedToolIds={selectedToolIds}
        onAddTool={handleAddTool}
        onRemoveTool={handleRemoveTool}
      />

      <AgentConfigAddSkillModal
        open={isAddSkillModalOpen}
        onOpenChange={setIsAddSkillModalOpen}
        selectedSkillIds={selectedSkillIds}
        onAddSkill={handleAddSkill}
        onRemoveSkill={handleRemoveSkill}
      />

      <AgentConfigAddSubagentModal
        open={isAddSubagentModalOpen}
        onOpenChange={setIsAddSubagentModalOpen}
        agentId={agentId}
        projectKey={projectKey}
        selectedSubagentIds={selectedSubagentIds}
        onAddSubagent={handleAddSubagent}
        onRemoveSubagent={handleRemoveSubagent}
      />

      <AgentConfigAddGuardrailModal
        open={isAddGuardrailModalOpen}
        onOpenChange={setIsAddGuardrailModalOpen}
        configuredGuardrailIds={Object.keys(configuredGuardrails)}
        onAddGuardrail={handleAddGuardrail}
        onRemoveGuardrail={handleRemoveGuardrail}
      />
    </div>
  );
}